import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, uploadImage, formatApiError } from "@/lib/api";
import { useCamera } from "@/components/CameraDialog";
import { WH_NAMES, useWh } from "@/lib/wh";

function useQueue(wh, lotId, category) {
  const [saved, setSaved] = useState(0);
  const [pending, setPending] = useState(0);
  const [thumbs, setThumbs] = useState([]);
  const chain = useRef(Promise.resolve());
  const add = (blob) => {
    setPending((p) => p + 1);
    setThumbs((t) => [URL.createObjectURL(blob), ...t].slice(0, 8));
    chain.current = chain.current.then(async () => {
      try {
        const path = await uploadImage(blob, "carta.jpg");
        await api.post("/wh/items", { wh, lot_id: lotId || null, photos: [path], category, quantity: 1 });
        setSaved((s) => s + 1);
      } catch (e) { toast.error(formatApiError(e)); }
      setPending((p) => p - 1);
    });
  };
  return { saved, pending, thumbs, add };
}

export default function WhQuick() {
  const { wh } = useWh();
  const nav = useNavigate();
  const [lots, setLots] = useState([]);
  const [lotId, setLotId] = useState("");
  const [category, setCategory] = useState("");
  const [flash, setFlash] = useState(false);
  const { videoRef, error } = useCamera(true, "environment");
  const q = useQueue(wh, lotId, category);
  useEffect(() => { api.get("/wh/lots", { params: { wh } }).then(({ data }) => setLots(data)).catch(() => {}); }, [wh]);
  const shoot = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    const scale = Math.min(1, 1600 / Math.max(v.videoWidth, v.videoHeight));
    c.width = v.videoWidth * scale;
    c.height = v.videoHeight * scale;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((b) => b && q.add(b), "image/jpeg", 0.85);
    setFlash(true);
    setTimeout(() => setFlash(false), 120);
    navigator.vibrate?.(30);
  };
  const finish = () => nav(lotId ? `/magazzino/lotti/${lotId}` : "/magazzino/cerca?lot=none");
  return (
    <div className="space-y-4 max-w-3xl">
      <Link to="/magazzino" className="text-sm text-[var(--muted)] flex items-center gap-1" data-testid="wh-back"><ArrowLeft className="h-4 w-4" /> {WH_NAMES[wh]}</Link>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Caricamento a raffica</h1>
      <p className="text-sm text-[var(--muted)]">Ogni scatto crea subito un articolo con la sua foto e i suoi codici. Alla fine rivedi, spunta e sposta tutto nel lotto giusto con un clic.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <select value={lotId} onChange={(e) => setLotId(e.target.value)} className="h-10 rounded-md border border-[var(--border)] px-3 text-sm bg-white" data-testid="quick-lot-select">
          <option value="">Da smistare (scegli il lotto dopo)</option>{lots.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <Input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={80} placeholder="Categoria per questi scatti (facoltativa)" data-testid="quick-category-input" />
      </div>
      {error ? <p className="text-sm text-red-600" data-testid="quick-camera-error">{error}</p> : (
        <div className="relative rounded-2xl overflow-hidden bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-[60dvh] object-contain" data-testid="quick-video" />
          {flash && <div className="absolute inset-0 bg-white/60" />}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button onClick={shoot} disabled={!!error} className="h-20 w-20 rounded-full bg-[var(--primary)] border-4 border-white shadow-xl grid place-items-center active:scale-95 transition-transform" aria-label="Scatta" data-testid="quick-shoot-button"><Camera className="h-8 w-8" /></button>
        <div className="text-sm" data-testid="quick-counter"><b className="text-2xl font-heading">{q.saved}</b> salvati{q.pending > 0 && <span className="ml-2 text-[var(--muted)] inline-flex items-center gap-1"><Loader2 className="h-4 w-4 animate-spin" /> {q.pending} in caricamento</span>}</div>
        <button onClick={finish} disabled={q.pending > 0} className="btn-secondary h-11 px-4 ml-auto" data-testid="quick-finish-button"><Check className="h-5 w-5" /> Fine e smista</button>
      </div>
      {q.thumbs.length > 0 && <div className="flex gap-2 overflow-x-auto" data-testid="quick-thumbs">{q.thumbs.map((t) => <img key={t} src={t} alt="" className="h-16 w-16 object-cover rounded-lg border shrink-0" />)}</div>}
    </div>
  );
}
